import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Users, Mail, KeyRound, X, CheckCircle, AlertCircle,
  Copy, Shield, Building2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OwnerAMCUsers = () => {
  const { amcId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [amc, setAmc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const myCaps = user?.owner_capabilities || {};
  const canReset = myCaps.is_super || myCaps.can_reset_amc_passwords;

  useEffect(() => {
    load();
  }, [amcId]);

  const load = async () => {
    try {
      setLoading(true);
      const [uRes, aRes] = await Promise.all([
        axios.get(`${API_URL}/owner/amcs/${amcId}/users`),
        axios.get(`${API_URL}/owner/amcs/${amcId}`),
      ]);
      setUsers(uRes.data);
      setAmc(aRes.data);
    } catch (err) {
      setToast({ type: 'error', msg: err.response?.data?.detail || 'Failed to load' });
    } finally {
      setLoading(false);
    }
  };

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 7000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/owner/amcs/${amcId}`)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                data-testid="back-to-amc"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="flex items-center space-x-3">
                <Users className="text-blue-600" size={32} />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">AMC Users</h1>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Building2 size={14} /> {amc?.name || amcId}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
          data-testid="amc-users-toast"
        >
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          <span className="text-sm whitespace-pre-wrap">{toast.msg}</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading users...</div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-6 py-3">User</th>
                  <th className="px-6 py-3">Role</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                      No users yet in this AMC.
                    </td>
                  </tr>
                )}
                {users.map((u) => (
                  <tr key={u.id} data-testid={`amc-user-row-${u.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{u.name}</div>
                      <div className="text-sm text-gray-500 flex items-center gap-1">
                        <Mail size={12} /> {u.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded capitalize">
                        <Shield size={10} /> {u.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {u.is_active ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Active</span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Deactivated</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {canReset && u.is_active && (
                        <button
                          onClick={() => setResetTarget(u)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg"
                          data-testid={`reset-pwd-${u.id}`}
                        >
                          <KeyRound size={14} /> Reset Password
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          onClose={() => setResetTarget(null)}
          onSuccess={(payload) => {
            setResetTarget(null);
            showToast(
              'success',
              `Password reset for ${payload.email}.\nTemporary password: ${payload.temp_password}\nShare securely — the user must change it on next login.`
            );
          }}
        />
      )}
    </div>
  );
};

const ResetPasswordModal = ({ user, onClose, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!autoGenerate && newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!window.confirm(`Reset password for ${user.email}? They will be forced to change it on next login.`)) return;
    setSubmitting(true);
    try {
      const payload = autoGenerate ? {} : { new_password: newPassword };
      const res = await axios.post(`${API_URL}/owner/amc-users/${user.id}/reset-password`, payload);
      onSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <form onSubmit={submit}>
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-3">
              <KeyRound className="text-orange-600" size={24} />
              <h2 className="text-xl font-bold text-gray-900">Reset Password</h2>
            </div>
            <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
              <X size={18} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800">
              <strong>{user.name}</strong><br />
              <span className="text-xs">{user.email}</span>
            </div>

            {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio" checked={autoGenerate} onChange={() => setAutoGenerate(true)}
                  data-testid="pwd-auto-radio"
                />
                <span className="text-sm">Generate a secure temporary password automatically</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio" checked={!autoGenerate} onChange={() => setAutoGenerate(false)}
                  data-testid="pwd-manual-radio"
                />
                <span className="text-sm">Set a specific password</span>
              </label>
            </div>

            {!autoGenerate && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="pwd-manual-input"
                  placeholder="Min 8 characters"
                />
              </div>
            )}

            <p className="text-xs text-gray-500">
              The user will be forced to change the password on their next login.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 p-6 border-t bg-gray-50">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg">
              Cancel
            </button>
            <button
              type="submit" disabled={submitting}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-orange-300"
              data-testid="submit-pwd-reset"
            >
              {submitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OwnerAMCUsers;
